import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { METRIC_KEY, type MetricDef } from "contracts/report/catalog";
import { CONTENT_FITNESS_DIMENSIONS } from "contracts/report/content-fitness/dimensions";
import { healthState } from "contracts/report/health";
import { For, Show } from "solid-js";
import { Delta } from "../../ui/Delta";
import { healthLabel, healthVar } from "../../ui/health";
import { Sparkline } from "../../ui/Sparkline";
import { ThresholdBar } from "../../ui/ThresholdBar";
import { contentFitnessCoverageDetail } from "./scanModel";

type SignalRow = OverviewResponse["signals"][number];

const CONTENT_FITNESS_KEYS = new Set<string>(
  CONTENT_FITNESS_DIMENSIONS.flatMap((dimension) => dimension.key),
);

export function SignalGroup(props: {
  websiteId: string;
  title: string;
  kicker: string;
  description: string;
  metrics: readonly MetricDef[];
  byKey: Map<string, SignalRow>;
  href?: string;
  primary?: boolean;
}) {
  return (
    <section class={`sig-group ${props.primary ? "is-primary" : ""}`}>
      <div class="sig-group-heading">
        <div>
          <span>{props.kicker}</span>
          <h2>{props.title}</h2>
          <p>{props.description}</p>
        </div>
        <Show when={props.href}>
          {(href) => <a href={href()}>Open diagnostic</a>}
        </Show>
      </div>
      <div class="sig-rows card">
        <For each={props.metrics}>
          {(metric) => (
            <SignalRowView
              websiteId={props.websiteId}
              metric={metric}
              signal={props.byKey.get(metric.key)}
            />
          )}
        </For>
      </div>
    </section>
  );
}

function SignalRowView(props: {
  websiteId: string;
  metric: MetricDef;
  signal: SignalRow | undefined;
}) {
  const score = () => props.signal?.score ?? null;
  const state = () =>
    healthState(score(), props.metric.good, props.metric.watch);
  const contentFitnessCoverage = () =>
    CONTENT_FITNESS_KEYS.has(props.metric.key) ? props.signal?.coverage : null;
  const detail = () => {
    const coverage = contentFitnessCoverage();
    return coverage
      ? contentFitnessCoverageDetail(coverage)
      : (props.signal?.detail ?? "Not assessed in this scan");
  };
  const href = () =>
    CONTENT_FITNESS_KEYS.has(props.metric.key)
      ? `/w/${props.websiteId}/scan/content-fitness/${props.metric.key}?from=scorecard`
      : `/w/${props.websiteId}/scan/metrics/${props.metric.key}`;
  return (
    <a class="sig-row" href={href()}>
      <div class="sig-name">
        <span class="tile-dot" style={{ background: healthVar(state()) }} />
        <div>
          <div class="sig-label">{props.metric.label}</div>
          <div class="sig-detail">{detail()}</div>
        </div>
      </div>
      <div class="sig-score mono">{score() ?? "--"}</div>
      <Show
        when={score() !== null}
        fallback={<span class="sig-delta-empty mono">--</span>}
      >
        <Delta value={props.signal?.delta ?? null} />
      </Show>
      <div class="sig-spark">
        <Sparkline
          data={props.signal?.trend ?? []}
          width={96}
          height={28}
          color={healthVar(state())}
          area={false}
          dot={false}
          strokeWidth={1.5}
          id={`sig-${props.metric.key}`}
        />
      </div>
      <div class="sig-thr">
        <ThresholdBar
          score={score()}
          good={props.metric.good}
          watch={props.metric.watch}
          target={props.metric.target}
          state={state()}
        />
      </div>
      <span class={`sig-state st-${state()}`}>{healthLabel(state())}</span>
    </a>
  );
}

export function SearchCoverage(props: { byKey: Map<string, SignalRow> }) {
  const hasCwv = props.byKey.has(METRIC_KEY.CWV);
  const hasAiAccess =
    props.byKey.has(METRIC_KEY.INCLUSION) ||
    props.byKey.has(METRIC_KEY.SOURCE_CONTROL);
  return (
    <section class="sig-coverage-note">
      <h3>Current diagnostic coverage</h3>
      <dl>
        <div>
          <dt>Core Web Vitals</dt>
          <dd>{hasCwv ? "Available" : "Not assessed"}</dd>
        </div>
        <div>
          <dt>AI crawler access and source structure</dt>
          <dd>{hasAiAccess ? "Available in AI Reference" : "Not assessed"}</dd>
        </div>
        <div>
          <dt>Indexability, titles, metadata and internal linking</dt>
          <dd>No dedicated diagnostic score</dd>
        </div>
      </dl>
    </section>
  );
}
